/**
 * A keyed, coalescing, retrying write queue. No React, no HTTP, no gradebook -
 * a job is an opaque `() => Promise`, and everything here is about WHEN to run
 * it and what to do when it fails. `useSyncQueue` wraps this for the sheet;
 * `tests/gradebook/sync-queue.test.js` exercises it directly.
 *
 * WHY IT EXISTS. The sheet writes optimistically: the number appears the instant
 * it is typed and the request goes out in parallel, because a spreadsheet must
 * never wait on a server. That part was right. Firing the request straight from
 * the input's `onChange` and swallowing failures into `console.error` was not,
 * and it bought three problems:
 *
 *   1. A request PER KEYSTROKE. Typing `100` sent `1`, `10`, `100` - three
 *      writes for one edit, two of them values the teacher never meant to save.
 *   2. Out-of-order writes. Those three raced, and whichever landed last won.
 *      `1` arriving after `100` leaves the wrong mark in the database
 *      permanently, with the right one still on screen.
 *   3. Silent data loss. A dropped connection left the mark rendered exactly as
 *      if it had saved, and nothing told the teacher otherwise.
 *
 * One rule fixes all three: PER KEY, AT MOST ONE REQUEST IN FLIGHT AND AT MOST
 * ONE EDIT WAITING BEHIND IT. A newer edit replaces the waiting one rather than
 * queuing another - last write wins, because the teacher's latest keystroke is
 * the only value that was ever meant to be saved. `1`/`10`/`100` collapses to a
 * single `100`, and two writes for one cell can never race.
 *
 * Failures retry with backoff and, when they give up, become reportable state
 * instead of a console line.
 */

/** Quiet period before a key's newest value is sent. */
export const SYNC_DEBOUNCE_MS = 500;

/** Attempts per key before its failure is surfaced. */
export const MAX_ATTEMPTS = 4;

const BACKOFF_BASE_MS = 600;
const BACKOFF_MAX_MS = 8000;

export const IDLE_STATUS = Object.freeze({ state: "saved", pending: 0, failed: 0 });

export function createSyncQueue({
  debounceMs = SYNC_DEBOUNCE_MS,
  maxAttempts = MAX_ATTEMPTS,
  onStatus = () => {},
} = {}) {
  /**
   * key -> {
   *   run      the newest writer waiting to be sent, or null when nothing waits
   *   timer    pending debounce or backoff timeout
   *   inflight a request for this key is outstanding
   *   attempts consecutive failures; reset by any success or fresh edit
   *   failed   gave up - `run` still holds the writer so Retry can re-send it
   * }
   */
  const jobs = new Map();
  let status = IDLE_STATUS;

  /** Recompute the summary, and report it only when it actually changed. */
  const publish = () => {
    let pending = 0;
    let failed = 0;
    for (const job of jobs.values()) {
      if (job.failed) failed += 1;
      else if (job.run || job.inflight) pending += 1;
    }
    const state = failed > 0 ? "error" : pending > 0 ? "saving" : "saved";
    if (status.state === state && status.pending === pending && status.failed === failed) return;
    status = { state, pending, failed };
    onStatus(status);
  };

  const schedule = (key, delay) => {
    const job = jobs.get(key);
    if (!job) return;
    if (job.timer) clearTimeout(job.timer);
    job.timer = setTimeout(() => flush(key), delay);
  };

  function flush(key) {
    const job = jobs.get(key);
    if (!job) return;
    job.timer = null;
    /* Nothing waiting, or this key's previous request has not come back yet. In
     * the second case the settle handler re-schedules, and dropping out here is
     * precisely what holds the one-request-per-key rule. */
    if (job.inflight || !job.run) return;

    const run = job.run;
    job.run = null;
    job.inflight = true;
    publish();

    Promise.resolve()
      .then(run)
      .then(
        () => {
          job.inflight = false;
          job.attempts = 0;
          job.failed = false;
          /* An edit landed while this was in flight - send that one next. */
          if (job.run) schedule(key, debounceMs);
          else jobs.delete(key);
          publish();
        },
        (error) => {
          job.inflight = false;
          job.attempts += 1;
          /* Put the failed writer back only if nothing newer arrived while it
           * was out; a newer edit supersedes it and is what should be sent. */
          if (!job.run) job.run = run;

          if (job.attempts >= maxAttempts) {
            job.failed = true;
            job.error = error;
          } else {
            schedule(key, Math.min(BACKOFF_BASE_MS * 2 ** (job.attempts - 1), BACKOFF_MAX_MS));
          }
          publish();
        },
      );
  }

  return {
    getStatus: () => status,

    /**
     * Record an edit. `run` is called later and must return a promise; it should
     * close over the value being written, since the queue re-sends the exact
     * function it was handed.
     */
    enqueue(key, run) {
      let job = jobs.get(key);
      if (!job) {
        job = { run: null, timer: null, inflight: false, attempts: 0, failed: false };
        jobs.set(key, job);
      }
      /* Coalesce: the newest value for a key is the only one worth sending, and
       * a fresh edit clears that key's failed state - the teacher is retrying by
       * hand. */
      job.run = run;
      job.failed = false;
      job.attempts = 0;
      if (job.timer) {
        clearTimeout(job.timer);
        job.timer = null;
      }
      if (!job.inflight) schedule(key, debounceMs);
      publish();
    },

    /**
     * Drop queued work whose key matches - for when the row it belongs to is
     * being deleted. Without this, deleting a student with an unsent mark leaves
     * a write that can only 404, and the sheet would report a save error for a
     * cell that no longer exists.
     *
     * Requests already in flight cannot be recalled; their outcome is discarded
     * rather than surfaced, which is right: the row is gone.
     */
    cancel(predicate) {
      for (const [key, job] of jobs) {
        if (!predicate(key)) continue;
        if (job.timer) clearTimeout(job.timer);
        job.timer = null;
        job.run = null;
        job.failed = false;
        if (!job.inflight) jobs.delete(key);
      }
      publish();
    },

    /** Re-send everything that gave up. Drives the indicator's Retry. */
    retryFailed() {
      for (const [key, job] of jobs) {
        if (!job.failed) continue;
        job.failed = false;
        job.attempts = 0;
        schedule(key, 0);
      }
      publish();
    },

    /**
     * Send every waiting edit now, skipping the debounce. Used on unmount so
     * navigating away mid-keystroke does not drop the last mark typed.
     */
    flushNow() {
      for (const key of [...jobs.keys()]) {
        const job = jobs.get(key);
        if (job?.timer) {
          clearTimeout(job.timer);
          job.timer = null;
        }
        flush(key);
      }
    },
  };
}
